/**
 * Common utility for handling column-level filters with cascading behavior
 * Provides reusable functions for filter management, localStorage caching, and API integration
 */

/**
 * Get filter cache key for a specific table
 * @param {string} tableKey - Unique identifier for the table (e.g., 'supply_shipment', 'supply_orders')
 * @returns {string} Cache key for localStorage
 */
export const getFilterCacheKey = (tableKey) => `columnFilters_${tableKey}`;

/**
 * Get initial filter model from localStorage
 * @param {string} tableKey - Unique identifier for the table
 * @returns {object|undefined} Parsed filter model or undefined if not found
 */
export const getInitialFilterModel = (tableKey) => {
  const cacheKey = getFilterCacheKey(tableKey);
  const savedFilters = localStorage.getItem(cacheKey);
  
  if (savedFilters) {
    try {
      return JSON.parse(savedFilters);
    } catch (error) {
      console.error(`Error parsing saved filters for ${tableKey}:`, error);
      return undefined;
    }
  }
  return undefined;
};

/**
 * Save filter model to localStorage
 * @param {string} tableKey - Unique identifier for the table
 * @param {object} rawFilterModel - AG Grid filter model to save
 */
export const saveFilterModel = (tableKey, rawFilterModel) => {
  try {
    if (rawFilterModel) {
      const cacheKey = getFilterCacheKey(tableKey);
      localStorage.setItem(cacheKey, JSON.stringify(rawFilterModel));
    }
  } catch (error) {
    console.error(`Error saving filters for ${tableKey}:`, error);
  }
};

/**
 * Clear saved filters from localStorage
 * @param {string} tableKey - Unique identifier for the table
 */
export const clearSavedFilters = (tableKey) => {
  try {
    const cacheKey = getFilterCacheKey(tableKey);
    localStorage.removeItem(cacheKey);
  } catch (error) {
    console.error(`Error clearing filters for ${tableKey}:`, error);
  }
};

/**
 * Create a cascading filter parameter configuration for AG Grid Set Filter
 * This enables dynamic filtering where options are filtered based on other applied filters
 * 
 * @param {Function} fetchFilterOptionsFunc - API function to fetch filter options
 * @param {string} columnField - The field name of the current column
 * @param {object} currentFilters - Object containing currently applied filters
 * @returns {object} AG Grid filterParams configuration
 */
export const createCascadingFilterParams = (fetchFilterOptionsFunc, columnField, currentFilters) => ({
  values: async (params) => {
    // Get other filters excluding current column for cascading behavior
    const { [columnField]: _, ...otherFilters } = currentFilters;
    const options = await fetchFilterOptionsFunc(columnField, otherFilters);
    params.success(options);
  },
  suppressSelectAll: false,
  debounceMs: 200,
  refreshValuesOnOpen: true
});

/**
 * Create a date filter parameter configuration for AG Grid Date Filter
 * 
 * @returns {object} AG Grid filterParams configuration for date columns
 */
export const createDateFilterParams = () => ({
  comparator: (filterDate, cellValue) => {
    if (!cellValue) return -1;
    const cellDate = new Date(cellValue);
    if (filterDate.getTime() === cellDate.getTime()) return 0;
    return cellDate < filterDate ? -1 : 1;
  },
  buttons: ['clear', 'apply'],
  debounceMs: 200,
  filterPlaceholder: 'Select date...',
  maxValidYear: 2100,
  minValidYear: 2000,
  suppressAndOrCondition: true,
  maxNumConditions: 1
});

export const createNumberFilterParams = () => ({
  debounceMs: 300,
  suppressAndOrCondition: true,
  maxNumConditions: 1,
  buttons: ['clear', 'apply'],
  filterOptions: ['equals', 'lessThan', 'greaterThan'],
});


/**
 * Create a text filter parameter configuration for AG Grid Text Filter
 * Optimized for server-side filtering
 * 
 * @returns {object} AG Grid filterParams configuration for text columns
 */
export const createTextFilterParams = () => ({
  filterOptions: ['contains'],
  debounceMs: 500,
  suppressAndOrCondition: true,
  trimInput: true,
  caseSensitive: false
});

/**
 * Validate and filter column filters to only include columns that exist in the current table
 * This prevents cross-screen filter contamination (e.g., filters from Purchase Orders affecting Shipments)
 * 
 * @param {object} columnFilters - Raw column filters object
 * @param {Array} columns - Array of column definitions for the current table
 * @returns {object} Validated column filters with only existing columns
 */
export const validateColumnFilters = (columnFilters, columns) => {
  if (!columnFilters || typeof columnFilters !== 'object') return {};
  if (!Array.isArray(columns)) return columnFilters;
  
  const validFields = new Set(columns.map(col => col.field).filter(Boolean));
  const validated = {};
  
  Object.keys(columnFilters).forEach(key => {
    if (validFields.has(key)) {
      validated[key] = columnFilters[key];
    }
  });
  
  return validated;
};

/**
 * Sanitize root-level filters object to prevent cross-screen contamination.
 * Keeps only recognized root keys and validates columnFilters against current columns.
 * @param {object} filters - Raw filters object possibly containing extraneous keys
 * @param {Array} columns - Current screen column definitions
 * @returns {object} Sanitized filters object
 */
export const sanitizeRootFilters = (filters, columns) => {
  if (!filters || typeof filters !== 'object') return {};
  const allowedRootKeys = new Set([
    'states', 'selectedStates', 'exceptions', 'selectedExceptions',
    'columnFilters', 'search', 'download'
  ]);
  const result = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (allowedRootKeys.has(key)) {
      result[key] = value;
    }
  });
  // Always revalidate columnFilters
  result.columnFilters = validateColumnFilters(filters.columnFilters, columns);
  return result;
};

/**
 * Create column filter change handler
 * Handles saving filters to localStorage and notifying parent components
 * 
 * @param {string} tableKey - Unique identifier for the table
 * @param {Function} setCurrentFilters - State setter for current filters
 * @param {Function} onColumnFilterChanged - Optional callback for parent component
 * @returns {Function} Handler function for column filter changes
 */
export const createColumnFilterChangeHandler = (tableKey, setCurrentFilters, onColumnFilterChanged) => {
  return (columnFilters, rawFilterModel) => {
    // Save raw AG Grid filter model for UI restoration
    saveFilterModel(tableKey, rawFilterModel);
    
    // Update current filters for cascading
    setCurrentFilters(columnFilters);
    
    // Notify HeaderBar via global event so counts can refresh
    try {
      const payload = {
        tableKey,
        columnFilters,
      };
      window.dispatchEvent(new CustomEvent('columnFiltersChanged', { detail: payload }));
    } catch (e) {
      // no-op if dispatch fails
    }

    // Notify parent component if callback provided
    if (typeof onColumnFilterChanged === 'function') {
      onColumnFilterChanged(columnFilters);
    }
  };
};

/**
 * Create a column definition with cascading filter support
 * Helper to reduce boilerplate when defining columns with filters
 * 
 * @param {string} field - Column field name
 * @param {string} headerName - Column header display name
 * @param {number} width - Column width (optional)
 * @param {string} filterType - Filter type: 'set', 'date', 'text', or 'none'
 * @param {Function} fetchFilterOptionsFunc - API function to fetch filter options (required for 'set' filter)
 * @param {object} currentFilters - Current applied filters (required for 'set' filter)
 * @param {object} additionalConfig - Additional column configuration
 * @returns {object} Column definition object
 */
export const createColumnWithFilter = (
  field,
  headerName,
  width = 150,
  filterType = 'none',
  fetchFilterOptionsFunc = null,
  currentFilters = {},
  additionalConfig = {}
) => {
  const baseConfig = {
    field,
    headerName,
    width,
    ...additionalConfig
  };

  switch (filterType) {
    case 'set':
      if (!fetchFilterOptionsFunc) {
        console.warn(`fetchFilterOptionsFunc is required for set filter on column: ${field}`);
        return baseConfig;
      }
      return {
        ...baseConfig,
        filter: 'agSetColumnFilter',
        filterParams: createCascadingFilterParams(fetchFilterOptionsFunc, field, currentFilters)
      };
    
    case 'date':
      return {
        ...baseConfig,
        filter: 'agDateColumnFilter',
        filterParams: createDateFilterParams()
      };
    
    case 'text':
      return {
        ...baseConfig,
        filter: 'agTextColumnFilter',
        filterParams: createTextFilterParams()
      };
    case 'number':
    return {
      ...baseConfig,
      filter: 'agNumberColumnFilter',
      filterParams: createNumberFilterParams(),
    };
    
    default:
      return baseConfig;
  }
};
